import { Component, ViewChild, OnDestroy, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';

import { switchMap, tap } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { TmtLoggerService } from 'tmt-logger';

import { TestSuiteBaseService } from 'src/services/test-suite.base.service';
import { TestRunBaseService } from 'src/services/test-run.base.service';
import { TestRunViewModel, TestSuite } from 'src/models/testRun';
@Component({
	selector: 'app-test-suites',
	templateUrl: './test-suites.component.html',
	styleUrls: ['./test-suites.component.scss'],
})
export class TestSuitesComponent implements OnDestroy, AfterViewInit {
	private subscription: Subscription;

	displayedColumns: string[] = ['testRun.testRunUid', 'testRun.environment', 'testSuiteVersion', 'testRun.executionTime', 'testRun.regBy', 'testSuite.name', 'testSuite.version'];

	dataSource: MatTableDataSource<TestRunViewModel>;

	@ViewChild(MatPaginator) public set matPaginator(paginator: MatPaginator) {
		if (paginator && this.testSuites) this.dataSource.paginator = paginator;
	}

	@ViewChild(MatSort) public set sort(sort: MatSort) {
		if (this.dataSource) {
			this.dataSource.sort = sort;
		}
	}

	maxall = 10;

	testSuites: TestRunViewModel[];

	activeTestSuite: TestSuite;

	constructor(
		private testRunBaseService: TestRunBaseService,
		private testSuiteBaseService: TestSuiteBaseService,
		private route: ActivatedRoute,
		private router: Router,
		private loggerService: TmtLoggerService,
		private cdr: ChangeDetectorRef,
	) {}

	// sorting and paging does not work unless the fetch of test suites also is done in AfterViewInit
	ngAfterViewInit(): void {
		this.route.url.pipe(switchMap(segment => this.testSuiteBaseService.getLatestTestSuite(segment[1].path))).subscribe(ts => (this.activeTestSuite = ts));
		this.getTestSuites();
	}

	private getTestSuites(): void {
		this.subscription = this.route.url
			.pipe(
				tap(() => {
					this.cdr.detectChanges();
				}),
				switchMap(urlSegment => this.testRunBaseService.getTestRunsByTestSuiteUid(urlSegment[1].path)),
			)
			.subscribe(
				testsuitedata => {
					this.testSuites = testsuitedata;
					const dataSource = new MatTableDataSource<TestRunViewModel>(this.testSuites);
					// set a new sortingDataAccessor to be able to sort nested objects
					dataSource.sortingDataAccessor = (item, property) => {
						switch (property) {
							case 'testRun.testRunUid':
								return item.testRun.testRunUid;
							case 'testRun.environment':
								return item.testRun.environment;
							case 'testSuiteversion':
								return item.testSuiteVersion;
							case 'testRun.executionTime':
								return item.testRun.executionTime;
							case 'testRun.regBy':
								return item.testRun.regBy;
							case 'testSuite.itemNo':
								return Number(item.testSuite.itemNo?.split('-').pop() ?? 0);
							case 'testSuite.name':
								return item.testSuite.name;
							case 'testSuite.version':
								return item.testSuite.version;
							default:
								return item[property];
						}
					};
					// set a new filterPredicate function to be able to filter nested objects
					dataSource.filterPredicate = (data, filter: string) => {
						const accumulator = (currentTerm, key) => {
							return this.nestedFilterCheck(currentTerm, data, key);
						};
						const dataStr = Object.keys(data).reduce(accumulator, '').toLowerCase();
						const transformedFilter = filter.trim().toLowerCase();
						return dataStr.indexOf(transformedFilter) !== -1;
					};
					this.dataSource = dataSource;
				},
				error => {
					this.loggerService.logError(error);
				},
			);
	}

	public nestedFilterCheck(search, data, key) {
		if (typeof data[key] === 'object') {
			for (const k in data[key]) {
				if (data[key][k] !== null) {
					search = this.nestedFilterCheck(search, data[key], k);
				}
			}
		} else {
			search += data[key];
		}
		return search;
	}

	public applyFilter(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value;
		this.dataSource.filter = filterValue.trim().toLowerCase();
	}

	public getPageSizeOptions(): number[] {
		if (this.dataSource && this.dataSource.paginator && this.dataSource.paginator.length > this.maxall) {
			return [10, 20, this.dataSource.paginator.length];
		} else {
			return [10, 20];
		}
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	public navigateTo(row: any) {
		this.router.navigate(['/testresult/testrun/' + row.testRun.testRunUid]);
	}
}
